import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { eventsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { EventListItem } from '../models';
import AlertBox from '../components/AlertBox';
import { formatDateTimeEST } from '../utils/dateUtils';

const Events: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getAll({
        page: currentPage,
        limit: 6,
        search: searchTerm,
      });
      setEvents(response.data.events);
      setTotalPages(response.data.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvents();
  }, [fetchEvents]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const formatDate = (dateString: string) => {
    return formatDateTimeEST(dateString);
  };

  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `CAD $${price}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-yellow-50">
      <Helmet>
        <title>Events — SoundX724</title>
        <meta name="description" content="Browse upcoming Indian classical music concerts, recitals, and workshops. Find events near you and book tickets at SoundX724." />
        <link rel="canonical" href="https://www.soundx724.com/events" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Events — SoundX724" />
        <meta property="og:description" content="Browse upcoming Indian classical music concerts, recitals, and workshops. Find events near you and book tickets at SoundX724." />
        <meta property="og:url" content="https://www.soundx724.com/events" />
        <meta property="og:site_name" content="SoundX724" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-800 to-secondary-800 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl font-elegant font-bold mb-4">
            Swar Siddhi - Concerts
          </h1>
          <p className="text-xl text-primary-100 max-w-2xl mx-auto">
            Discover upcoming classical music concerts, recitals, and workshops
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {/* Search and Filter Section */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="max-w-md flex-1">
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {user && user.role === 'admin' && (
            <Link
              to="/events/new"
              className="bg-gradient-to-r from-primary-600 via-primary-700 to-warm-700 hover:from-primary-700 hover:via-primary-800 hover:to-warm-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              + Add Event
            </Link>
          )}
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🎵</div>
            <p className="text-secondary-600">Loading events...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <AlertBox type="error" message={error} />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎼</div>
            <h3 className="text-2xl font-semibold text-secondary-700 mb-2">No Events Found</h3>
            <p className="text-secondary-500">
              {searchTerm ? 'Try adjusting your search terms' : 'Check back soon for upcoming events'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                {/* Event Image */}
                <div className="h-48 bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                  {event.image_files ? (
                    <img 
                      src={event.image_files.startsWith('/') ? event.image_files : `/img/events-img/${event.image_files}`}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg className="w-20 h-20 text-white opacity-80" fill="none" viewBox="0 0 64 64" stroke="currentColor" strokeWidth="2">
                      <circle cx="48" cy="48" r="10" fill="#fff" fillOpacity="0.2" />
                      <path d="M44 12v32.5a6.5 6.5 0 1 0 2-4.6V16h12V12H44z" fill="#fff" fillOpacity="0.7" />
                      <rect x="44" y="12" width="14" height="4" rx="2" fill="#fff" fillOpacity="0.5" />
                      <path d="M44 12v32.5a6.5 6.5 0 1 0 2-4.6V16h12V12H44z" stroke="#fff" strokeWidth="2" />
                    </svg>
                  )}
                </div>

                {/* Event Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2 line-clamp-2">
                    {event.title}
                  </h3>
                  <p className="text-secondary-600 text-sm mb-4 line-clamp-3">
                    {event.description}
                  </p>

                  {/* Event Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-secondary-500">
                      <span className="mr-2">📅</span>
                      {formatDate(event.event_start_date)}
                    </div>
                    <div className="flex items-center text-sm text-secondary-500">
                      <span className="mr-2">📍</span>
                      {event.event_address}
                    </div>
                    <div className="flex items-center text-sm text-secondary-500">
                      <span className="mr-2">👤</span>
                      Organized by {event.organizer_name}
                    </div>
                  </div>

                  {/* Price and Action */}
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-2xl font-bold text-primary-600">
                      {formatPrice(event.event_ticket_price_adults)}
                    </span>
                    <div className="flex gap-2">
                      {new Date(event.event_start_date) > new Date() && (event.is_disabled === false) && (
                        <Link
                          to={`/book-tickets/${event.uuid}`}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm font-semibold"
                          title="Book Tickets"
                        >
                          Book
                        </Link>
                      )}
                      <Link
                        to={`/events/${event.uuid}`}
                        className="w-10 h-10 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                        title="View Details"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12 space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 hover:bg-secondary-50"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg ${
                  currentPage === page
                    ? 'bg-primary-600 text-white'
                    : 'border border-secondary-300 hover:bg-secondary-50'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-secondary-300 rounded-lg disabled:opacity-50 hover:bg-secondary-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;